import { useState } from "react";
import { formatDateTime } from "@excellent/ui";
import type { UniversalWorkDetail } from "@excellent/core/verification-universal-surfaces";
import { href } from "../../nav";
import { resolveReceiptVerdicts, verdictFrom } from "../../resolved-verdicts";
import { ToneBadge, Verdict } from "../../ui";

/** Read-only presentation. Ordering never grants authority or invents a repair link. */
export function WorkJourney({
  detail,
  now,
  onRefresh,
}: {
  detail: UniversalWorkDetail;
  now: string;
  onRefresh: () => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [showAll, setShowAll] = useState(false);
  const attempts = [
    ...new Map(
      [...detail.incomplete.attempts, ...detail.current.attempts].map((row) => [
        row.binding.attemptId,
        row,
      ]),
    ).values(),
  ].sort((a, b) => a.openedAt.localeCompare(b.openedAt));
  const receipts = [
    ...new Map(
      [...detail.incomplete.receipts, ...detail.current.receipts].map((row) => [
        row.payload.id,
        row,
      ]),
    ).values(),
  ].sort((a, b) => a.payload.issuedAt.localeCompare(b.payload.issuedAt));
  const currentAttempts = [...detail.current.attempts].sort((a, b) =>
    b.openedAt.localeCompare(a.openedAt),
  );
  const active = currentAttempts[0];
  const latest = [...detail.current.receipts].sort((a, b) =>
    b.payload.issuedAt.localeCompare(a.payload.issuedAt),
  )[0];
  const needsResult =
    active &&
    !detail.current.receipts.some(
      (row) => row.payload.binding.attemptId === active.binding.attemptId,
    );
  const resolved = resolveReceiptVerdicts(receipts, now);
  const lineage = new Set<string>();
  let cursor = active;
  while (cursor && !lineage.has(cursor.binding.attemptId)) {
    lineage.add(cursor.binding.attemptId);
    const child = cursor;
    cursor = attempts.find(
      (row) =>
        row.binding.attemptId === child.supersedesAttemptId &&
        row.binding.contractDigest === child.binding.contractDigest,
    );
  }
  const submitted = active?.state === "SUBMITTED";
  const canContinue = active?.state === "OPEN" || submitted;
  const nextTitle =
    needsResult && canContinue
      ? submitted
        ? "Next: request independent verification"
        : "Next: continue the worker attempt"
      : !latest
        ? "Next: prepare the worker handoff"
        : "Attempt and repair history";
  const instructions = [
    `Work: ${detail.work.title}`,
    `Work ID: ${detail.work.id}`,
    ...(active
      ? [
          `Attempt: ${active.binding.attemptId}`,
          `Worker: ${active.worker.actorId}`,
          `Accepted contract: ${active.binding.contractId} @ ${active.binding.contractVersion}`,
          `Contract digest: ${active.binding.contractDigest}`,
        ]
      : []),
    submitted
      ? "The worker output is submitted. Check for an existing verification operation before retrying. Ask the independent verification operator to collect and replay the accepted checks for this exact attempt."
      : "Resume the recorded attempt with the worker's existing authorized connection. Read the accepted contract, complete the allowed changes, and submit the output and evidence for independent verification.",
    "Keep this Work and attempt identity when reconnecting. Refresh the saved Work before creating any duplicate operation.",
  ].join("\n");
  async function refresh() {
    if (busy) return;
    setBusy(true);
    setMessage("");
    try {
      await onRefresh();
      setMessage("Status refreshed from the workspace. Review the latest recorded state below.");
    } catch {
      setMessage(
        "Could not refresh. Check your connection or sign in again, then retry on this saved Work.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section
      aria-label="Work progress and next step"
      data-work-journey=""
      className="min-w-0 rounded-xl border border-border bg-muted/20 p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="type-body font-semibold">{nextTitle}</h2>
        <button
          type="button"
          className="rounded-md border bg-background px-3 py-2 type-body disabled:opacity-50"
          disabled={busy}
          onClick={() => void refresh()}
        >
          {busy ? "Refreshing…" : "Refresh status"}
        </button>
      </div>
      {needsResult && canContinue ? (
        <div className="mt-3 space-y-3 type-body">
          <p>
            {submitted
              ? "The worker submitted output, but this attempt has no verification result yet. Ask the independent verification operator to check the accepted contract against this output."
              : "The attempt is open. Send the handoff to the assigned worker to continue the task and submit its output for verification. Opening an attempt does not launch the worker."}
          </p>
          <button
            type="button"
            className="rounded-md bg-primary px-3 py-2 font-medium !text-white"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(instructions);
                setMessage(
                  "Handoff copied. Send it to the assigned worker or verification operator.",
                );
              } catch {
                setMessage(
                  "Copy was unavailable. Expand Handoff and recovery details and copy the instructions manually.",
                );
              }
            }}
          >
            Copy {submitted ? "verification request" : "worker handoff"}
          </button>
          <details className="rounded-md border bg-background p-3">
            <summary className="cursor-pointer py-1">Handoff and recovery details</summary>
            <p className="my-3">
              If the connection was interrupted, refresh first. Resume the existing attempt or
              verification operation using these references; avoid starting a duplicate.
            </p>
            <pre className="whitespace-pre-wrap break-all type-subtext">{instructions}</pre>
          </details>
        </div>
      ) : !latest ? (
        <p className="mt-3 type-body">
          No result has been recorded. Review the accepted contract and worker status in Current,
          then arrange the next attempt with your worker. Refresh this saved Work after
          reconnecting.
        </p>
      ) : null}
      {message && (
        <p className="mt-3 type-body" role="status">
          {message}
        </p>
      )}
      {attempts.length > 0 && (
        <div className="mt-5">
          <p className="mb-3 type-subtext text-muted-foreground">
            Attempts in time order. A repair is linked only when the recorded predecessor and
            accepted contract match. Earlier results remain readable; a receipt's presence does not
            authorize completion.
          </p>
          <ol className="space-y-3">
            {attempts.map((attempt, index) => {
              if (!showAll && lineage.size > 0 && !lineage.has(attempt.binding.attemptId))
                return null;
              const predecessor = attempts.findIndex(
                (row) =>
                  row.binding.attemptId === attempt.supersedesAttemptId &&
                  row.binding.contractDigest === attempt.binding.contractDigest,
              );
              const results = receipts.filter(
                (row) => row.payload.binding.attemptId === attempt.binding.attemptId,
              );
              return (
                <li
                  key={attempt.binding.attemptId}
                  className="min-w-0 rounded-lg border bg-background p-4"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="type-body font-medium">
                      {predecessor >= 0
                        ? `Attempt ${index + 1} · repair of attempt ${predecessor + 1}`
                        : `Attempt ${index + 1}`}
                    </h3>
                    <ToneBadge tone="neutral">
                      {attempt.state === "SUBMITTED"
                        ? "Output submitted"
                        : attempt.state === "OPEN"
                          ? "Open · awaiting output"
                          : attempt.state}
                    </ToneBadge>
                  </div>
                  <p className="mt-2 break-all type-subtext">
                    {attempt.worker.actorId} · {formatDateTime(attempt.openedAt)}
                  </p>
                  <ul className="mt-3 space-y-2">
                    {results.map((receipt) => (
                      <li
                        key={receipt.payload.id}
                        className="flex flex-wrap items-center gap-2 type-body"
                      >
                        <Verdict status={verdictFrom(resolved, receipt, now)} />
                        <span>
                          {receipt.payload.id === latest?.payload.id
                            ? "Latest recorded result"
                            : "Earlier result"}
                        </span>
                        <a
                          className="underline"
                          href={href("receipts", encodeURIComponent(receipt.payload.id))}
                        >
                          Inspect receipt · {formatDateTime(receipt.payload.issuedAt)}
                        </a>
                      </li>
                    ))}
                    {!results.length && (
                      <li className="type-body text-muted-foreground">
                        {attempt.state === "OPEN"
                          ? "Awaiting worker output"
                          : "No verification receipt recorded"}
                      </li>
                    )}
                  </ul>
                  <details className="mt-3 type-subtext">
                    <summary className="cursor-pointer py-1">Attempt references</summary>
                    <p className="mt-2 break-all">{attempt.binding.attemptId}</p>
                    <p className="break-all">
                      Contract: {attempt.binding.contractId} · {attempt.binding.contractVersion}
                    </p>
                    {attempt.supersedesAttemptId && (
                      <p className="break-all">
                        Recorded predecessor: {attempt.supersedesAttemptId}
                      </p>
                    )}
                  </details>
                </li>
              );
            })}
          </ol>
          {attempts.length > lineage.size && lineage.size > 0 && (
            <button
              type="button"
              className="mt-3 rounded-md border bg-background px-3 py-2 type-body"
              onClick={() => setShowAll((value) => !value)}
            >
              {showAll
                ? "Show latest attempt and linked repairs"
                : `Show ${attempts.length - lineage.size} other recorded attempts`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
